// The listing filter bar against GET /items/filter-options (HUBX-8479 / HUBX-8487). All six
// controls take their choices from that response — Category and Sub Category from
// `categories`, Restrictions from `restrictions` — instead of the full `dropDowns.categories`
// lookup and the hardcoded Yes/No of `itemService.restrictionsFilters`.

import { ILookupCategory } from '../item-write/item.models';
import { INameId } from '../../interface/INameId';

/** Anything with a `categoryId`: a tree node, or the dropdowns' "All" entry (categoryId null). */
export interface ICategoryChoice {
  categoryId?: string | null;
  children?: readonly ICategoryChoice[];
}

/**
 * The `categoryIds` a category choice sends (HUBX-8398, unchanged by v2):
 *
 * - a Sub sends its own id;
 * - a Main with Subs — or "All" under Sub Category — sends the ids of ALL its Subs;
 * - a Main without Subs sends its own id;
 * - "All Categories" sends nothing.
 *
 * Expanded against the FULL tree of `/lookups/categories`, never against the pruned facet the
 * dropdown shows: the facet is cut down to the vendor's catalogue and moves after a write, so
 * a choice made before it would not cover a Sub the catalogue gained since.
 *
 * A Main is a leaf when `children.length === 0`. v2 sends `children: []`, never null — the
 * legacy test `!category.children` is true only for null, and against `[]` it sent an empty
 * `categoryIds`, which is no category filter at all.
 *
 * Until the full tree has loaded, the node the dropdown handed over is used as it is.
 */
export function categoryIdsForChoice(
  fullTree: readonly ILookupCategory[],
  root: ICategoryChoice | null | undefined,
  sub: ICategoryChoice | null | undefined
): string[] {
  if (sub?.categoryId) { return [sub.categoryId]; }
  if (!root?.categoryId) { return []; }

  const node: ICategoryChoice = fullTree.find(c => c.categoryId === root.categoryId) ?? root;
  const children = (node.children ?? []).map(c => c.categoryId).filter((id): id is string => !!id);
  return children.length ? children : [root.categoryId];
}

/**
 * What Sub Category offers: the chosen Main's `children` in the tree just received, in order,
 * matched by `categoryId` — so it follows the vendor's catalogue on every refetch. A Main no
 * longer in the tree (a write took its last item) offers none; the chosen Sub then stays shown
 * and sent (see the ticket, "A chosen value can fall outside its list").
 */
export function subCategoriesOf(tree: readonly ILookupCategory[], rootId: string | null | undefined): ILookupCategory[] {
  if (!rootId) { return []; }
  return [...(tree.find(c => c.categoryId === rootId)?.children ?? [])];
}

const RESTRICTION_LABELS: Readonly<Record<string, string>> = { yes: 'Yes', no: 'No' };

/** Restrictions as the dropdown binds it (`id`/`name`): `yes` shown as "Yes", `no` as "No",
 *  and the value itself sent back as `restrictions`. Only the values the server listed — the
 *  ones that would return rows — in the order served; one it would not parse is dropped. */
export function restrictionOptions(values: readonly string[]): INameId[] {
  return values
    .filter(v => RESTRICTION_LABELS[v] !== undefined)
    .map(v => ({ id: v, name: RESTRICTION_LABELS[v] }));
}
